/**
 * TTS player (F3) — a sequential audio queue for `tts.chunk` events.
 *
 * The backend emits translated speech as a stream of `tts.chunk` events, each
 * carrying a base64-encoded audio blob (`audio_b64`), its MIME type (`mime`,
 * e.g. `audio/mpeg`) and a per-segment sequence number (`seq`). This player:
 *
 *  - decodes each chunk (base64 → Blob → object URL) and
 *  - plays them ONE AT A TIME with no overlap: a single HTMLAudioElement plays
 *    the head of the queue, and the next chunk starts only on the `ended`
 *    event. Queueing keeps playback ordered and prevents the "everyone talking
 *    at once" artifact when chunks arrive faster than real time.
 *
 * Output-device routing uses `HTMLMediaElement.setSinkId`, which is
 * Chromium-only (Chrome/Edge). On Safari/Firefox the call is skipped and audio
 * plays on the system default output.
 */

/** A decoded chunk waiting in the queue. */
interface QueuedChunk {
  /** Object URL of the decoded audio blob; revoked after playback. */
  url: string;
  seq: number;
}

/** Decode a base64 string into a typed byte array backed by a plain ArrayBuffer. */
function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** True when the running browser supports selecting an output device. */
export function supportsSinkSelection(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

export interface TtsPlayerOptions {
  /** Called on a decode/playback failure for a single chunk (non-fatal). */
  onError?: ((message: string) => void) | undefined;
}

/**
 * A sequential, no-overlap audio queue. Create one per consumer (host or a
 * viewer page) and call `dispose()` when the surface unmounts.
 */
export class TtsPlayer {
  private readonly audio: HTMLAudioElement;
  private readonly queue: QueuedChunk[] = [];
  private readonly onError: ((message: string) => void) | undefined;

  private currentUrl: string | null = null;
  private playing = false;
  private muted = false;
  private disposed = false;
  private unlocked = false;
  private pendingSinkId: string | null = null;

  constructor(options: TtsPlayerOptions = {}) {
    this.onError = options.onError;
    this.audio = new Audio();
    this.audio.preload = 'auto';
    this.audio.addEventListener('ended', this.handleEnded);
    this.audio.addEventListener('error', this.handleAudioError);
  }

  /** Whether the element has been unlocked by a user gesture. */
  get isUnlocked(): boolean {
    return this.unlocked;
  }

  /**
   * Satisfy the browser autoplay policy. MUST be called from within a user
   * gesture (e.g. a click handler): it plays a short silent clip on the shared
   * <audio> element, which the browser then treats as user-initiated, so every
   * later `play()` for real TTS chunks is allowed. Without this, the first
   * `play()` rejects with "Reprodução de áudio bloqueada pelo navegador".
   * Idempotent and safe to call repeatedly.
   */
  async unlock(): Promise<void> {
    if (this.disposed || this.unlocked) return;
    // A 1-frame silent WAV (44-byte header + no samples) as a data URL.
    const SILENT_WAV =
      'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
    const probe = new Audio(SILENT_WAV);
    probe.muted = true;
    try {
      await probe.play();
      probe.pause();
      this.unlocked = true;
    } catch {
      // Still not permitted (no gesture) — leave unlocked=false; the caller can
      // retry from another gesture.
    }
  }

  /** Whether this player currently suppresses playback. */
  get isMuted(): boolean {
    return this.muted;
  }

  /** Number of chunks waiting (excludes the one currently playing). */
  get queueLength(): number {
    return this.queue.length;
  }

  /** Decode and enqueue a `tts.chunk` payload. Playback starts if idle. */
  enqueue(audioB64: string, mime: string, seq: number): void {
    if (this.disposed) return;
    try {
      const bytes = base64ToBytes(audioB64);
      const blob = new Blob([bytes], { type: mime || 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      this.queue.push({ url, seq });
    } catch {
      this.onError?.('Falha ao decodificar o áudio de TTS.');
      return;
    }
    if (!this.playing) {
      void this.playNext();
    }
  }

  /**
   * Select the audio OUTPUT device (Chromium only). No-op on browsers without
   * `setSinkId`; the id is remembered and re-applied to the element. Pass an
   * empty string to fall back to the system default.
   */
  async setSinkId(deviceId: string): Promise<void> {
    this.pendingSinkId = deviceId;
    if (typeof this.audio.setSinkId !== 'function') return;
    try {
      await this.audio.setSinkId(deviceId);
    } catch {
      this.onError?.('Não foi possível alterar o dispositivo de saída de áudio.');
    }
  }

  /** Mute or unmute playback without draining the queue. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.audio.muted = muted;
  }

  /** Stop playback, drop every queued chunk, and revoke their URLs. */
  stop(): void {
    this.audio.pause();
    this.releaseCurrent();
    while (this.queue.length > 0) {
      const chunk = this.queue.shift();
      if (chunk) URL.revokeObjectURL(chunk.url);
    }
    this.playing = false;
  }

  /** Permanently tear down the player. Safe to call more than once. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stop();
    this.audio.removeEventListener('ended', this.handleEnded);
    this.audio.removeEventListener('error', this.handleAudioError);
  }

  private async playNext(): Promise<void> {
    if (this.disposed) return;
    const chunk = this.queue.shift();
    if (!chunk) {
      this.playing = false;
      return;
    }
    this.playing = true;
    this.currentUrl = chunk.url;
    this.audio.src = chunk.url;
    this.audio.muted = this.muted;

    // Re-apply a sink selection made before the element had a source.
    if (this.pendingSinkId !== null && typeof this.audio.setSinkId === 'function') {
      try {
        await this.audio.setSinkId(this.pendingSinkId);
      } catch {
        /* already surfaced via setSinkId(); keep playing on default output */
      }
    }

    try {
      await this.audio.play();
    } catch {
      // Autoplay policy or a decode failure — skip this chunk, keep the queue.
      this.onError?.('Reprodução de áudio bloqueada pelo navegador.');
      this.advance();
    }
  }

  private readonly handleEnded = (): void => {
    this.advance();
  };

  private readonly handleAudioError = (): void => {
    this.onError?.('Erro ao reproduzir o áudio de TTS.');
    this.advance();
  };

  /** Release the finished chunk and move on to the next one. */
  private advance(): void {
    this.releaseCurrent();
    void this.playNext();
  }

  private releaseCurrent(): void {
    if (this.currentUrl) {
      URL.revokeObjectURL(this.currentUrl);
      this.currentUrl = null;
    }
  }
}
